import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, FileDown, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, openPdf } from "@/lib/api";
import { act, useFetch } from "@/lib/hooks";
import { toLocalInput } from "@/lib/format";
import { PageHeader, StatusBadge } from "@/components/common";
import { ClientVehiclePicker } from "@/components/forms";
import { Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const startOfWeek = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 1) % 7));
  return x;
};

function NewAppointment({ open, onOpenChange, onSaved, defaultStart }: any) {
  const [sel, setSel] = useState<any>({ client: null, vehicle: null });
  const [start, setStart] = useState(defaultStart);
  const [dur, setDur] = useState(60);
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (open) {
      setSel({ client: null, vehicle: null });
      setStart(defaultStart);
      setReason("");
    }
  }, [open, defaultStart]);
  const save = async () => {
    if (!sel.vehicle) return toast.error("Choisissez un client et un véhicule");
    const ok = await act(() => api.post("/appointments", { client_id: sel.client.id, vehicle_id: sel.vehicle.id, start, duration_min: dur, reason }), "Rendez-vous enregistré");
    if (ok) {
      onSaved();
      onOpenChange(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="appointment-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase">Nouveau rendez-vous</DialogTitle></DialogHeader>
        <ClientVehiclePicker value={sel} onChange={setSel} />
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Date et heure"><Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} data-testid="appointment-start-input" /></Field>
          <Field label="Durée (min)"><Input type="number" value={dur} onChange={(e) => setDur(Number(e.target.value))} data-testid="appointment-duration-input" /></Field>
          <Field label="Motif"><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Vidange, bruit freins…" data-testid="appointment-reason-input" /></Field>
        </div>
        <Button className="h-11" onClick={save} data-testid="appointment-save-btn">Enregistrer le rendez-vous</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function Agenda() {
  const [week, setWeek] = useState(startOfWeek(new Date()));
  const [open, setOpen] = useState(false);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => new Date(week.getTime() + i * 86400000)), [week]);
  const from = toLocalInput(week);
  const to = toLocalInput(new Date(week.getTime() + 7 * 86400000));
  const { data, reload } = useFetch<any[]>(`/appointments?date_from=${from}&date_to=${to}`);
  const setStatus = async (id: string, status: string) => {
    if (await act(() => api.patch(`/appointments/${id}`, { status }), "Rendez-vous mis à jour")) reload();
  };
  const defaultStart = toLocalInput(new Date(new Date().setHours(9, 0, 0, 0)));
  const printPlanning = (a: Date, b: Date) => openPdf("planning", "", `/planning/pdf?date_from=${toLocalInput(a)}&date_to=${toLocalInput(b)}`).catch((e) => toast.error(errMsg(e)));
  return (
    <div>
      <PageHeader eyebrow="Planning" title="Agenda des rendez-vous"
        actions={<>
          <Button variant="outline" size="icon" className="h-11 w-11" onClick={() => setWeek(new Date(week.getTime() - 7 * 86400000))} data-testid="agenda-prev-week"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" className="h-11" onClick={() => setWeek(startOfWeek(new Date()))} data-testid="agenda-today">Cette semaine</Button>
          <Button variant="outline" size="icon" className="h-11 w-11" onClick={() => setWeek(new Date(week.getTime() + 7 * 86400000))} data-testid="agenda-next-week"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="outline" className="h-11" onClick={() => printPlanning(week, new Date(week.getTime() + 7 * 86400000))} data-testid="agenda-week-pdf-btn"><FileDown className="mr-2 h-4 w-4" />PDF semaine</Button>
          <Button className="h-11" onClick={() => setOpen(true)} data-testid="new-appointment-btn"><Plus className="mr-2 h-4 w-4" />Rendez-vous</Button>
        </>} />
      <div className="grid gap-px overflow-hidden rounded-sm border border-border bg-border md:grid-cols-4 xl:grid-cols-7" data-testid="agenda-grid">
        {days.map((d) => {
          const key = toLocalInput(d).slice(0, 10);
          const items = (data || []).filter((a) => a.start.startsWith(key));
          const today = key === toLocalInput(new Date()).slice(0, 10);
          const weekend = d.getDay() === 5;
          return (
            <div key={key} className={`min-h-[260px] p-3 ${weekend ? "bg-background" : "bg-card"}`} data-testid={`agenda-day-${key}`}>
              <div className={`mb-3 flex items-baseline justify-between border-b pb-2 ${today ? "border-primary" : "border-border"}`}>
                <span className="font-display text-sm font-bold uppercase">{d.toLocaleDateString("fr-FR", { weekday: "long" })}</span>
                <span className="flex items-center gap-2 text-xs text-muted-foreground">
                  {d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}
                  <button onClick={() => printPlanning(d, new Date(d.getTime() + 86400000))} title="PDF du jour" className="rounded-sm p-1 transition-colors hover:bg-accent hover:text-foreground" data-testid={`agenda-day-pdf-${key}`}><FileDown className="h-3.5 w-3.5" /></button>
                </span>
              </div>
              <div className="space-y-2">
                {items.map((a) => (
                  <div key={a.id} className="rounded-sm border border-border bg-background p-2.5 text-xs" data-testid={`appointment-${a.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono-data font-semibold">{a.start.slice(11, 16)}</span>
                      <StatusBadge status={a.status} />
                    </div>
                    <div className="mt-1 font-mono-data">{a.plate}</div>
                    <div className="text-muted-foreground">{a.client_name} · {a.reason}</div>
                    {a.status === "prevu" && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        <Link to={`/receptions/nouvelle?vehicle=${a.vehicle_id}&appointment=${a.id}`} className="rounded-sm bg-primary px-2 py-1 font-semibold text-white" data-testid={`appointment-receive-${a.id}`}>Réceptionner</Link>
                        <button onClick={() => setStatus(a.id, "absent")} className="rounded-sm border border-border px-2 py-1 hover:bg-accent" data-testid={`appointment-absent-${a.id}`}>Absent</button>
                        <button onClick={() => setStatus(a.id, "annule")} className="rounded-sm border border-border px-2 py-1 hover:bg-accent" data-testid={`appointment-cancel-${a.id}`}>Annuler</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <NewAppointment open={open} onOpenChange={setOpen} onSaved={reload} defaultStart={defaultStart} />
    </div>
  );
}
